import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  updateContactGroup,
  addContactsToGroup,
  removeContactsFromGroup,
  importContactsFromCSV,
  fetchContactGroups,
} from "../../redux/Slice/Contacts/contactGroupsSlice";
import { fetchContacts } from "../../redux/Slice/Contacts/contactsSlice";

// Groups (datasets) only support WhatsApp contacts — same rule as ContactGroupDetails.jsx.
const isWhatsAppContact = (contact) => (contact.platform || "whatsapp") === "whatsapp";

const parseFileOnClient = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const formatted = rows
          .slice(1) // skip header row
          .map((row) => ({
            name: row[0] || "Unknown",
            phoneNumberID: row[1] ? String(row[1]).trim().replace(/\D/g, "") : "",
          }))
          .filter((c) => c.name !== "Unknown" && c.phoneNumberID);
        resolve(formatted);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });

const EditDatasetPanel = ({ group, onSaved }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.contactGroups);
  const { contacts } = useSelector((state) => state.contacts);

  const [name, setName] = useState(group.name);
  const [removedContactIds, setRemovedContactIds] = useState([]);
  const [addMode, setAddMode] = useState(null); // null | "import" | "existing"
  const [csvFile, setCsvFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLabelFilter, setSelectedLabelFilter] = useState("");
  const [newContactIds, setNewContactIds] = useState([]);

  useEffect(() => {
    if (addMode === "existing") {
      dispatch(fetchContacts({ page: 1, limit: 1000, sortDirection: "asc" }));
    }
  }, [addMode, dispatch]);

  const currentContacts = useMemo(
    () => (group.contacts || []).filter((c) => !removedContactIds.includes(c._id)),
    [group.contacts, removedContactIds]
  );

  const currentContactIds = useMemo(
    () => new Set((group.contacts || []).map((c) => c._id)),
    [group.contacts]
  );

  const pickableContacts = useMemo(
    () => contacts.filter((c) => isWhatsAppContact(c) && !currentContactIds.has(c._id)),
    [contacts, currentContactIds]
  );

  const availableLabels = useMemo(() => {
    const labelSet = new Set();
    pickableContacts.forEach((contact) => {
      if (Array.isArray(contact.labels)) {
        contact.labels.forEach((label) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [pickableContacts]);

  const filteredPickableContacts = useMemo(() => {
    return pickableContacts.filter((contact) => {
      if (selectedLabelFilter && !contact.labels?.includes(selectedLabelFilter)) {
        return false;
      }
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return contact.name?.toLowerCase().includes(term) || contact.phoneNumberID?.includes(term);
    });
  }, [pickableContacts, searchTerm, selectedLabelFilter]);

  const toggleRemoveContact = (contactId) => {
    setRemovedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const toggleNewContact = (contactId) => {
    setNewContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const allowedExtensions = [".csv", ".xlsx", ".xls"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Please select a valid CSV or Excel file (.csv, .xlsx, .xls).");
      return;
    }
    setCsvFile(file);
    setFileName(file.name);
  };

  const hasChanges =
    name.trim() !== group.name ||
    removedContactIds.length > 0 ||
    newContactIds.length > 0 ||
    (addMode === "import" && !!csvFile);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a dataset name");
      return;
    }

    try {
      if (name.trim() !== group.name) {
        await dispatch(
          updateContactGroup({ groupId: group._id, updateData: { name: name.trim() } })
        ).unwrap();
      }

      if (removedContactIds.length > 0) {
        await dispatch(
          removeContactsFromGroup({ groupId: group._id, contactIds: removedContactIds })
        ).unwrap();
      }

      if (newContactIds.length > 0) {
        await dispatch(
          addContactsToGroup({ groupId: group._id, contactIds: newContactIds })
        ).unwrap();
      }

      if (addMode === "import" && csvFile) {
        const parsedContacts = await parseFileOnClient(csvFile);
        if (parsedContacts.length > 0) {
          await dispatch(
            importContactsFromCSV({ groupId: group._id, contacts: parsedContacts })
          ).unwrap();
        }
      }

      dispatch(fetchContactGroups());
      toast.success(`Dataset "${name.trim()}" saved.`);
      setRemovedContactIds([]);
      setNewContactIds([]);
      setCsvFile(null);
      setFileName("");
      setAddMode(null);
      onSaved?.();
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message || "Failed to save dataset.";
      toast.error(msg);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Dataset
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <TextField
          label="Dataset Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          size="small"
          margin="normal"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="500" gutterBottom>
          Contacts ({currentContacts.length})
        </Typography>
        <List
          dense
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            mb: 2,
          }}
        >
          {currentContacts.length === 0 ? (
            <ListItem>
              <ListItemText primary="No contacts in this dataset" />
            </ListItem>
          ) : (
            currentContacts.map((contact) => (
              <ListItem
                key={contact._id}
                secondaryAction={
                  <IconButton size="small" onClick={() => toggleRemoveContact(contact._id)}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                }
              >
                <ListItemText primary={contact.name || "Unknown"} secondary={contact.phoneNumberID} />
              </ListItem>
            ))
          )}
        </List>

        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Button
            variant={addMode === "import" ? "contained" : "outlined"}
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => setAddMode(addMode === "import" ? null : "import")}
            sx={{ textTransform: "none", borderRadius: "2rem" }}
          >
            Import Contacts
          </Button>
          <Button
            variant={addMode === "existing" ? "contained" : "outlined"}
            size="small"
            onClick={() => setAddMode(addMode === "existing" ? null : "existing")}
            sx={{ textTransform: "none", borderRadius: "2rem" }}
          >
            Add Contact
          </Button>
        </Box>

        {addMode === "import" && (
          <Box
            sx={{
              border: (theme) => `2px dashed ${theme.palette.primary.main}`,
              borderRadius: 2,
              padding: 2,
              textAlign: "center",
            }}
          >
            {!csvFile ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload a CSV or Excel file with contact name and phone number
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  size="small"
                  sx={{ mt: 1, textTransform: "none", borderRadius: "2rem" }}
                >
                  Choose File
                  <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
                </Button>
              </>
            ) : (
              <>
                <Chip label={fileName} color="success" variant="outlined" sx={{ mb: 1 }} />
                <Box>
                  <Button
                    size="small"
                    onClick={() => {
                      setCsvFile(null);
                      setFileName("");
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Remove
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {addMode === "existing" && (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search contacts by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              {availableLabels.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Label</InputLabel>
                  <Select
                    value={selectedLabelFilter}
                    label="Label"
                    onChange={(e) => setSelectedLabelFilter(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Labels</em>
                    </MenuItem>
                    {availableLabels.map((label) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {newContactIds.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {newContactIds.length} selected
              </Typography>
            )}
            <List
              dense
              sx={{
                maxHeight: 220,
                overflowY: "auto",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {filteredPickableContacts.length === 0 ? (
                <ListItemButton disabled>
                  <ListItemText primary="No contacts found" />
                </ListItemButton>
              ) : (
                filteredPickableContacts.map((contact) => (
                  <ListItemButton key={contact._id} onClick={() => toggleNewContact(contact._id)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        edge="start"
                        checked={newContactIds.includes(contact._id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText primary={contact.name || "Unknown"} secondary={contact.phoneNumberID} />
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
        <Button onClick={handleSave} variant="contained" disabled={loading || !hasChanges}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default EditDatasetPanel;
